import { speechChunks } from "@vladrss/core";
import type { Playing, Speaker, SpeechAudio } from "./speaker";

export interface SpokenArticle { id: string; title: string; source: string; paragraphs: string[]; }
export interface ListenState { active: boolean; id: string; title: string; source: string; block: number; playing: boolean; loading: boolean; voice: string; rate: number; }
interface Dependencies {
  speaker: Speaker;
  load(id: string, signal: AbortSignal): Promise<SpokenArticle | null>;
  eligible(id: string): boolean;
  unread(id: string): boolean;
  finished(id: string): void;
  article(item: SpokenArticle, previous: string): void;
  error(message: string): void;
}
const initial: ListenState = { active: false, id: "", title: "", source: "", block: -1, playing: false, loading: false, voice: "", rate: 1 };

/** Playback intent, queue and cancellation are independent of the article being browsed. */
export class Listener {
  state: ListenState = initial;
  private listeners = new Set<() => void>();
  subscribe = (fn: () => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; };
  private set(patch: Partial<ListenState>) { this.state = { ...this.state, ...patch }; this.listeners.forEach(fn => fn()); }
  private abort?: AbortController;
  private playback?: Playing;
  private ids: string[] = [];
  private index = 0;
  private cache = new Map<string, SpokenArticle>();
  private started = 0;
  constructor(private deps: Dependencies) {}
  start(ids: string[], block = -1, first?: SpokenArticle) {
    this.stop(); this.ids = [...ids]; this.cache.clear(); if (first) this.cache.set(first.id, first);
    this.deps.speaker.begin(); this.set({ playing: true }); this.run(0, block);
  }
  stop() { this.abort?.abort(); this.abort = undefined; this.playback = undefined; this.cache.clear(); this.ids = []; this.set({ ...initial, rate: this.state.rate }); }
  pause() { this.set({ playing: false }); this.playback?.pause(); }
  resume() { if (!this.state.active) return; this.set({ playing: true }); this.playback?.resume(); }
  toggle() { if (this.state.playing) this.pause(); else this.resume(); }
  rate(value: number) { this.set({ rate: value }); this.playback?.rate(value); }
  blockText(block: number) { return this.cache.get(this.state.id)?.paragraphs[block]; }
  seek(block: number) { if (!this.state.active) return; this.set({ playing: true }); this.run(this.index, block); }
  previous() { this.seek(Date.now() - this.started > 3000 ? this.state.block : Math.max(-1, this.state.block - 1)); }
  next() {
    if (!this.state.active) return;
    let at = this.index + 1;
    while (at < this.ids.length && (!this.deps.eligible(this.ids[at]!) || !this.deps.unread(this.ids[at]!))) at++;
    this.run(at, -1);
  }
  invalidate() { if (this.state.active && !this.deps.eligible(this.state.id)) this.next(); }
  private async wait(signal: AbortSignal) {
    signal.throwIfAborted(); if (this.state.playing) return;
    await new Promise<void>((resolve, reject) => {
      const clear = () => { off(); signal.removeEventListener("abort", cancelled); };
      const cancelled = () => { clear(); reject(signal.reason); };
      const off = this.subscribe(() => { if (this.state.playing) { clear(); resolve(); } });
      signal.addEventListener("abort", cancelled, { once: true });
    });
  }
  private run(index: number, block: number) {
    const previous = this.state.id;
    this.abort?.abort(); this.playback = undefined;
    const controller = new AbortController(); this.abort = controller;
    const signal = controller.signal;
    this.set({ active: true, id: this.ids[index] ?? "", title: "", loading: true });
    void this.read(index, block, signal, previous).catch(error => {
      if (signal.aborted) return;
      this.stop(); this.deps.error((error as Error).message || "Listen couldn't continue");
    });
  }
  private async read(index: number, firstBlock: number, signal: AbortSignal, previous: string) {
    for (let at = index; at < this.ids.length; at++) {
      const id = this.ids[at]!; this.index = at;
      if (!this.deps.eligible(id) || (at > index && !this.deps.unread(id))) continue;
      this.set({ id, block: -1, title: "", loading: true });
      const item = this.cache.get(id) ?? await this.deps.load(id, signal); signal.throwIfAborted();
      if (!item || !item.paragraphs.length || !this.deps.eligible(id) || ((at > index || (previous && previous !== id)) && !this.deps.unread(id))) continue;
      const advancing = previous && previous !== id;
      this.cache.clear(); this.cache.set(id, item); this.deps.article(item, previous); previous = id;
      this.set({ title: item.title, source: item.source });
      const pieces = [...speechChunks(`${advancing ? `Next, from ${item.source}. ` : ""}${item.title}`).map(text => ({ block: -1, text })), ...item.paragraphs.flatMap((text, block) => speechChunks(text).map(text => ({ block, text })))];
      const prepared = new Map<number, Promise<SpeechAudio>>();
      const prepare = (n: number) => {
        let p = prepared.get(n);
        if (!p) { p = this.deps.speaker.prepare(pieces[n]!.text, signal); prepared.set(n, p); void p.catch(() => {}); }
        return p;
      };
      const start = at === index ? pieces.findIndex(p => p.block >= firstBlock) : 0;
      for (let n = Math.max(0, start); n < pieces.length; n++) {
        await this.wait(signal); this.set({ block: pieces[n]!.block, loading: true });
        let source = await prepare(n); signal.throwIfAborted(); await this.wait(signal);
        if (source.kind === "cloud" && !this.deps.speaker.cloud) source = this.deps.speaker.fallback(pieces[n]!.text);
        // Prove the first request succeeds before speculating, then keep only two pieces ahead.
        if (this.deps.speaker.cloud) for (let j = n + 1; j <= Math.min(n + 2, pieces.length - 1); j++) void prepare(j);
        for (;;) {
          signal.throwIfAborted(); this.set({ loading: false, voice: source.kind === "cloud" ? "Speechify" : "Browser voice" });
          this.started = Date.now();
          let playback: Playing | undefined;
          try {
            playback = this.deps.speaker.play(source, this.state.rate, signal); this.playback = playback;
            await playback.done; break;
          }
          catch (error) {
            signal.throwIfAborted();
            if (this.playback === playback) this.playback = undefined;
            if ((error as Error).name === "NotAllowedError") { this.pause(); this.deps.error("Press Play to allow audio in this browser"); await this.wait(signal); }
            else if (source.kind === "cloud") { source = this.deps.speaker.fallback(pieces[n]!.text); await this.wait(signal); }
            else throw error;
          } finally { if (this.playback === playback) this.playback = undefined; }
        }
        prepared.delete(n);
      }
      signal.throwIfAborted(); if (this.deps.eligible(id)) this.deps.finished(id);
    }
    signal.throwIfAborted(); this.stop();
  }
}
